'use client';

import { useState } from 'react';
import { ShoppingBag } from 'lucide-react';
import type { MerchProductImage } from '../interfaces/merch';

interface ProductImageGalleryProps {
  images: MerchProductImage[];
  productName: string;
  fallbackUrl?: string;
}

function resolveGallery(images: MerchProductImage[], fallbackUrl?: string) {
  if (images.length > 0) {
    return [...images].sort((a, b) => a.display_order - b.display_order);
  }
  if (fallbackUrl) {
    return [
      {
        id: 'fallback',
        product_id: '',
        url: fallbackUrl,
        display_order: 0,
        is_primary: true,
      },
    ];
  }
  return [];
}

export function ProductImageGallery({
  images,
  productName,
  fallbackUrl,
}: ProductImageGalleryProps) {
  const gallery = resolveGallery(images, fallbackUrl);
  const [activeIndex, setActiveIndex] = useState(0);
  const active = gallery[activeIndex];

  if (!active) {
    return (
      <div className="flex aspect-[4/5] items-center justify-center rounded-2xl border border-white/10 bg-muted/20">
        <ShoppingBag className="h-16 w-16 text-muted-foreground/30" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="group relative aspect-[4/5] overflow-hidden rounded-2xl border border-white/10 bg-muted/10">
        <img 
          src={active.url} 
          alt={`${productName} - imagen ${activeIndex + 1}`} 
          loading={activeIndex === 0 ? "eager" : "lazy"}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" 
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
      </div>

      {gallery.length > 1 && (
        <div className="flex gap-3 overflow-x-auto pb-1">
          {gallery.map((image, index) => (
            <button
              key={image.id}
              type="button"
              onClick={() => setActiveIndex(index)}
              className={`relative h-20 w-20 shrink-0 overflow-hidden rounded-xl border transition-all ${
                index === activeIndex
                  ? 'border-[var(--merch-accent)] ring-2 ring-[color-mix(in_srgb,var(--merch-accent)_40%,transparent)]'
                  : 'border-white/10 opacity-70 hover:opacity-100'
              }`}
            >
              <img 
                src={image.url} 
                alt={`${productName} miniatura ${index + 1}`} 
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover" 
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
